import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { QueryKey } from '@/Config/api/queryKey';
import { ActionsApi } from '@/Domain/api/Action/ApiAction/Actions.api';
import { ActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { ShopifyMcpDto } from '@/Domain/api/Action/dto/ShopifyMcp.dto';

export const useActivateShopifyMcp = (action: ActionDto) => {
  const { t } = useTranslation('shopify_mcp');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: [QueryKey.actions.basic.patch],
    mutationFn: ({ is_active, id }: { is_active: boolean; id: number }) =>
      ActionsApi.update(id, { is_active: is_active }),
    onSuccess: (updatedAction) => {
      // Only patch an existing detail entry; seeding a partial one (no shopify_url)
      // would make ensureQueryData skip the real fetch and prefill an empty form.
      void queryClient.setQueryData(
        [QueryKey.actions.shopifyMcp.get, updatedAction.id],
        (prev: ShopifyMcpDto | undefined) => (prev ? { ...prev, ...updatedAction } : prev),
      );
      void queryClient.invalidateQueries({ queryKey: ['subscription'] });

      queryClient.setQueryData([QueryKey.actions.basic.list], (prev: ActionDto[]) =>
        prev.map((actionItem) =>
          actionItem.id === action.id
            ? { ...actionItem, is_active: updatedAction.is_active }
            : actionItem,
        ),
      );

      if (updatedAction.is_active) {
        toast.info(t('toast.active_enabled'));
      } else {
        toast.info(t('toast.active_disabled'));
      }
    },
  });

  const handleActive = (isActive: boolean) => {
    mutation.mutate({ is_active: isActive, id: action.id });
  };

  return {
    handleActive,
  };
};
